import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import type { PersonnelProject, TMTimesheet } from "@/hooks/usePersonnelProjects";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

interface Props {
  project: PersonnelProject;
  timesheets: TMTimesheet[];
}

export function PersonnelInvoicesTab({ project, timesheets }: Props) {
  const isMobile = useIsMobile();
  const invoicedSheets = timesheets.filter((t) => t.status === "invoiced" || t.status === "paid");

  return (
    <div className="space-y-4 mt-4">
      <h2 className="text-base font-semibold">Invoices</h2>
      {invoicedSheets.length === 0 ? (
        <p className="text-center py-8 text-muted-foreground">No invoices generated yet</p>
      ) : isMobile ? (
        <div className="space-y-3">
          {invoicedSheets.map((t, i) => (
            <Card key={t.id}>
              <CardContent className="p-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm font-medium">{project.project_number}-W{String(i + 1).padStart(2, "0")}</span>
                  <Badge variant="outline" className={t.status === "paid" ? "bg-emerald-500/10 text-emerald-700" : ""}>
                    {t.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{format(new Date(t.week_ending), "MMM d, yyyy")}</span>
                  <span className="text-lg font-bold">{fmt(t.total_billed)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Invoice #</TableHead>
                  <TableHead>Week Ending</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {invoicedSheets.map((t, i) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-mono">{project.project_number}-W{String(i + 1).padStart(2, "0")}</TableCell>
                    <TableCell>{format(new Date(t.week_ending), "MMM d, yyyy")}</TableCell>
                    <TableCell className="text-right">{fmt(t.total_billed)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={t.status === "paid" ? "bg-emerald-500/10 text-emerald-700" : ""}>
                        {t.status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
